import { useEffect, useRef } from 'react';
import type MapEngine from '@/map-core';
import { tileLayer } from '../utils/config';

/**
 * 统一图层响应式管理
 *
 * 层级：hooks（"React 生命周期"）
 *
 * 职责：
 * 1. 根据 mapId / mapVersion / hiddenWmsLayerIds 响应式创建/更新/清理 WMS 瓦片图层
 * 2. 根据 ldMapVisible / isReady / options 响应式创建/清理矢量瓦片图层（LD 地图）
 * 3. 将原来 useWmsLayer + useVectorTileLayer 合并为单一入口
 *
 * 不关心：业务搜索条件、绘制状态、覆盖物样式
 */
export function useMapLayers(
  mapEngineRef: React.MutableRefObject<MapEngine | null>,
  isReady: boolean,
  mapId: string | null | undefined,
  mapVersion: string | null | undefined,
  hiddenWmsLayerIds: string[],
  ldMapVisible: boolean,
  vectorTileOptions?: { url: string; minZoom?: number; maxZoom?: number } | null
) {
  const vectorTileLayerRef = useRef<any>(null);
  const vectorTileUrlRef = useRef<string | null>(null);

  // ---- WMS 图层 ----
  useEffect(() => {
    if (!isReady || mapId == null || mapVersion == null || !mapEngineRef.current) {
      return;
    }

    const WMS_GROUP_LAYER_ID = 'HIGH_PRECISION_WMS_GROUP_LAYER';
    const wmsLayerIds = new Set<string>(tileLayer.map((item) => item.layerId));
    const disabledWmsLayers = hiddenWmsLayerIds.filter((id) => wmsLayerIds.has(id));

    // 清理历史遗留的单层 WMS layer，避免叠加残留
    mapEngineRef.current.clearLayer(WMS_GROUP_LAYER_ID);
    for (const item of tileLayer) {
      mapEngineRef.current.clearLayer(item.layerId);
    }

    const enabledItems = tileLayer.filter(
      (item) => !disabledWmsLayers.includes(item.layerId)
    );
    if (enabledItems.length === 0) return;

    const uniqueUrls = new Set(enabledItems.map((item) => item.url));
    const cql = `map_id=${mapId} and map_version=${mapVersion}`;

    if (uniqueUrls.size === 1) {
      const url = enabledItems[0].url;
      const layersParam = enabledItems.map((item) => item.params?.LAYERS).join(',');
      const stylesParam = enabledItems.map((item) => item.params?.STYLES ?? '').join(',');
      const cqlParam = enabledItems.map(() => cql).join(';');

      const layer = mapEngineRef.current.createWmsLayer({
        url,
        params: {
          FORMAT: 'image/png',
          TILED: true,
          LAYERS: layersParam,
          STYLES: stylesParam,
          CQL_FILTER: cqlParam,
        },
        layerId: WMS_GROUP_LAYER_ID,
      });
      mapEngineRef.current.addLayers([{ layer, layerId: WMS_GROUP_LAYER_ID }]);
      return;
    }

    for (const item of enabledItems) {
      const layer = mapEngineRef.current.createWmsLayer({
        url: item.url,
        params: { ...item.params, CQL_FILTER: cql },
        layerId: item.layerId,
      });
      mapEngineRef.current.addLayers([{ layer, layerId: item.layerId }]);
    }
  }, [mapId, mapVersion, isReady, hiddenWmsLayerIds]);

  // ---- 矢量瓦片图层（LD 地图）----
  useEffect(() => {
    if (!isReady || !mapEngineRef.current || !vectorTileOptions) return;

    const currentUrl = vectorTileOptions.url;

    // URL 发生变化时，移除旧图层并重新创建
    if (
      vectorTileLayerRef.current &&
      vectorTileUrlRef.current !== null &&
      vectorTileUrlRef.current !== currentUrl
    ) {
      mapEngineRef.current.removeVectorTileLayer(vectorTileLayerRef.current);
      vectorTileLayerRef.current = null;
      vectorTileUrlRef.current = null;
    }

    // 首次需要时创建图层
    if (!vectorTileLayerRef.current) {
      const layer = mapEngineRef.current.addVectorTileLayer({
        url: currentUrl,
        minZoom: vectorTileOptions.minZoom,
        maxZoom: vectorTileOptions.maxZoom,
      });
      vectorTileLayerRef.current = layer;
      vectorTileUrlRef.current = currentUrl;
      // 若初始状态为隐藏，创建后立即移除
      if (!ldMapVisible) {
        mapEngineRef.current.setVectorTileLayerVisible(layer, false);
      }
      return;
    }

    // 图层已存在且 URL 未变，直接切换显隐
    mapEngineRef.current.setVectorTileLayerVisible(
      vectorTileLayerRef.current,
      ldMapVisible
    );
  }, [isReady, vectorTileOptions?.url, ldMapVisible]);

  // 组件卸载时统一清理 LD 图层
  useEffect(() => {
    return () => {
      if (vectorTileLayerRef.current && mapEngineRef.current) {
        mapEngineRef.current.removeVectorTileLayer(vectorTileLayerRef.current);
        vectorTileLayerRef.current = null;
        vectorTileUrlRef.current = null;
      }
    };
  }, []);
}
